"use client";

import { useDroppable, UniqueIdentifier } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { SortableItem } from "./SortableItem";
import { TierLabel } from "./TierLabel";
import { twMerge } from "tailwind-merge";

interface DroppableContainerProps {
  id: string;
  items: string[]; // Array of item IDs
  imageUrls: Record<string, string>;
  itemNames: Record<string, string>;
  onDeleteItem: (itemId: string) => void;
  tier?: {
    label: string;
    color: {
      label: string;
      background: string;
    };
    fontSizeMultiplier?: number;
  };
  onLabelChange?: (newLabel: string) => void;
  onFontSizeChange?: (multiplier: number) => void;
  deleteIcon?: "remove" | "delete";
  deleteLabel?: string;
  activeId?: UniqueIdentifier | null;
  overId?: UniqueIdentifier | null;
  isEditMode?: boolean;
  selectedItems?: Set<string>;
  onToggleItemSelection?: (itemId: string, isShiftClick: boolean) => void;
  isPresentationMode?: boolean;
}

export function DroppableContainer({
  id,
  items,
  imageUrls,
  itemNames,
  onDeleteItem,
  tier,
  onLabelChange,
  onFontSizeChange,
  deleteIcon = "remove",
  deleteLabel = "Remove item",
  activeId,
  overId,
  isEditMode = false,
  selectedItems,
  onToggleItemSelection,
  isPresentationMode = false,
}: DroppableContainerProps) {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  // Check if we're hovering over this container or any of its items
  const isOverContainer =
    isOver || (overId != null && items.includes(overId as string));

  if (tier) {
    // Tier container (horizontal layout)
    return (
      <div
        className="grid grid-cols-subgrid col-span-2 rounded-lg overflow-hidden transition-shadow duration-300"
        style={{
          minHeight: 'calc(var(--tiers-root-size) * 6.25)',
          backgroundColor: `${tier.color.background}20`,
          border: `2px solid ${tier.color.background}`,
          boxShadow:
            isOverContainer && activeId
              ? `0px 2px 10px ${tier.color.background}BB`
              : `0px 2px 6px ${tier.color.background}66`,
        }}
      >
        {/* Tier Label */}
        <TierLabel
          label={tier.label}
          color={tier.color}
          fontSizeMultiplier={tier.fontSizeMultiplier}
          onLabelChange={onLabelChange}
          onFontSizeChange={onFontSizeChange}
          isPresentationMode={isPresentationMode}
        />

        {/* Drop Zone with SortableContext */}
        <div
          ref={setNodeRef}
          className="relative bg-gray-400/20 rounded-md"
          style={{
            margin: 'calc(var(--tiers-root-size) * 0.5)',
            padding: 'calc(var(--tiers-root-size) * 0.75)',
            backgroundColor: isOverContainer
              ? `${tier.color.background}88`
              : `${tier.color.background}22`,
            transition: "background-color 0.2s, border 0.2s",
            border:
              isOverContainer && activeId
                ? `2px solid ${tier.color.background}`
                : "2px solid transparent",
          }}
        >
          <SortableContext items={items} strategy={rectSortingStrategy}>
            <div
              style={{
                gap: 'calc(var(--tiers-root-size) * 0.5)',
                minHeight: items.length === 0 && !isPresentationMode ? 'calc(var(--tiers-root-size) * 5)' : undefined,
              }}
              className={twMerge(
                "flex flex-wrap items-start content-start",
                items.length === 0 &&
                  !isPresentationMode &&
                  "items-center justify-center content-center"
              )}
            >
              {items.length === 0 && !isPresentationMode ? (
                <div
                  className="w-full h-full flex items-center justify-center font-medium text-shadow-lg text-shadow-black/25"
                  style={{
                    color: tier.color.background,
                    fontSize: 'calc(var(--tiers-root-size) * 1.5)',
                  }}
                >
                  Drop items here
                </div>
              ) : (
                items.map((itemId) => (
                  <SortableItem
                    key={itemId}
                    id={itemId}
                    imageUrl={imageUrls[itemId] || null}
                    name={itemNames[itemId] || ""}
                    onDelete={() => onDeleteItem(itemId)}
                    deleteIcon={deleteIcon}
                    deleteLabel={deleteLabel}
                  />
                ))
              )}
            </div>
          </SortableContext>
        </div>
      </div>
    );
  }

  // Unassigned items (sidebar - vertical layout)
  const itemsContent = (
    <div
      className="grid"
      style={{
        gap: 'calc(var(--tiers-root-size) * 0.5)',
        minHeight: 'calc(var(--tiers-root-size) * 5)',
        gridTemplateColumns: "repeat(auto-fit, calc(var(--tiers-root-size) * 5))",
        justifyContent: "space-around",
      }}
    >
      {items.length === 0 ? (
        <div
          className="flex items-center justify-center text-muted-foreground"
          style={{
            gridColumn: "1 / -1",
            fontSize: 'calc(var(--tiers-root-size) * 0.875)',
            marginTop: 'calc(var(--tiers-root-size) * -2.5)',
          }}
        >
          Upload images or drag items here
        </div>
      ) : (
        items.map((itemId) => (
          <SortableItem
            key={itemId}
            id={itemId}
            imageUrl={imageUrls[itemId] || null}
            name={itemNames[itemId] || ""}
            onDelete={() => onDeleteItem(itemId)}
            deleteIcon={deleteIcon}
            deleteLabel={deleteLabel}
            isEditMode={isEditMode}
            isSelected={selectedItems?.has(itemId)}
            onToggleSelection={onToggleItemSelection}
          />
        ))
      )}
    </div>
  );

  return (
    <div
      ref={!isEditMode ? setNodeRef : undefined}
      style={{
        padding: 'calc(var(--tiers-root-size) * 1)',
      }}
      className="relative h-full"
    >
      {!isEditMode ? (
        <SortableContext items={items} strategy={rectSortingStrategy}>
          {itemsContent}
        </SortableContext>
      ) : (
        itemsContent
      )}
    </div>
  );
}
